import type {
  ConfigValidationErrors,
  InsightConfig,
  InsightConfigJsonField,
  InsightConfigJsonFieldSpec,
  InsightConfigMeta,
} from '../models/insightConfig.types'

import { InsightConfigServiceError, normalizePipelineSteps } from './insightConfigPipeline'
import { cloneInsightValue, insightValuesEqual, isInsightRecord } from './insightValue'

export const DEFAULT_REFRESH_INTERVAL_SECONDS = 79_200

export const EDITABLE_INSIGHT_CONFIG_FIELDS = [
  'enum',
  'label',
  'status',
  'outcome_category',
  'severity',
  'confidence',
  'platform',
  'title_template',
  'content_template',
  'template_variables',
  'detection_config',
  'detection_sources',
  'lookback_days',
  'eligibility_config',
  'impact_formula',
  'pipeline_steps',
  'llm_config',
  'content_type',
  'column_definitions',
  'hidden_dimensions',
  'tab_definitions',
  'visualization_config',
  'default_refresh_interval_seconds',
  'priority',
] as const satisfies readonly (keyof InsightConfig)[]

export type EditableInsightConfigField = (typeof EDITABLE_INSIGHT_CONFIG_FIELDS)[number]
export type EditableInsightConfigPayload = Pick<InsightConfig, EditableInsightConfigField>

export const INSIGHT_CONFIG_JSON_FIELD_SPECS = {
  template_variables: spec('template_variables', 'array', false, []),
  detection_config: spec('detection_config', 'object', false, {}),
  detection_sources: spec('detection_sources', 'array', true, null),
  eligibility_config: spec('eligibility_config', 'object', true, null),
  impact_formula: spec('impact_formula', 'object', true, null),
  llm_config: spec('llm_config', 'object', true, null),
  column_definitions: spec('column_definitions', 'array', false, []),
  hidden_dimensions: spec('hidden_dimensions', 'array', false, []),
  tab_definitions: spec('tab_definitions', 'array', true, null),
  visualization_config: spec('visualization_config', 'object', true, null),
} satisfies Record<InsightConfigJsonField, InsightConfigJsonFieldSpec>

export const INSIGHT_CONFIG_JSON_FIELDS = Object.keys(
  INSIGHT_CONFIG_JSON_FIELD_SPECS,
) as InsightConfigJsonField[]

interface ApiErrorFieldsLike {
  fields?: Record<string, string[]>
  payload?: unknown
  detail?: string
  message?: string
}

function spec(
  field: InsightConfigJsonField,
  kind: InsightConfigJsonFieldSpec['kind'],
  nullable: boolean,
  defaultValue: unknown,
): InsightConfigJsonFieldSpec {
  return { field, kind, nullable, defaultValue }
}

function normalizeArray(value: unknown, fallback: unknown[]): unknown[] {
  return Array.isArray(value) ? value : fallback
}

function normalizeNullableArray(value: unknown, fallback: unknown[] | null): unknown[] | null {
  return value === null ? null : normalizeArray(value, fallback ?? [])
}

function normalizeObject(
  value: unknown,
  fallback: Record<string, unknown>,
): Record<string, unknown> {
  return isInsightRecord(value) ? value : fallback
}

function normalizeNullableObject(
  value: unknown,
  fallback: Record<string, unknown> | null,
): Record<string, unknown> | null {
  return value === null ? null : normalizeObject(value, fallback ?? {})
}

function normalizeConfigPlatform(
  value: unknown,
  fallback: InsightConfig['platform'],
): InsightConfig['platform'] {
  if (Array.isArray(value)) {
    const platforms = value.filter(
      (item): item is string => typeof item === 'string' && !!item.trim(),
    )
    return platforms.length > 0 ? platforms.map((item) => item.trim()) : fallback
  }
  return normalizePlatformValue(value, {
    fallback: typeof fallback === 'string' ? fallback : 'all',
  })
}

export function createEmptyInsightConfig(meta: Partial<InsightConfigMeta> = {}): InsightConfig {
  return {
    enum: '',
    label: '',
    status: meta.status_choices?.[0]?.value ?? 'active',
    outcome_category: '',
    severity: '',
    confidence: '',
    platform: 'all',
    title_template: '',
    content_template: '',
    template_variables: [],
    detection_config: {},
    detection_sources: null,
    lookback_days: 28,
    eligibility_config: null,
    impact_formula: null,
    pipeline_steps: normalizePipelineSteps(
      meta.default_pipeline_steps,
      meta.default_pipeline_steps,
    ),
    llm_config: null,
    content_type: meta.content_type_choices?.[0]?.value ?? 'table_description',
    column_definitions: [],
    hidden_dimensions: [],
    tab_definitions: null,
    visualization_config: null,
    default_refresh_interval_seconds: DEFAULT_REFRESH_INTERVAL_SECONDS,
    priority: 0,
  }
}

export function normalizeInsightConfig(
  config: Partial<InsightConfig> = {},
  meta: Partial<InsightConfigMeta> = {},
): InsightConfig {
  const base = createEmptyInsightConfig(meta)
  const next = cloneInsightValue(config)
  return {
    ...base,
    ...next,
    platform: normalizeConfigPlatform(next.platform, base.platform),
    template_variables: normalizeArray(next.template_variables, base.template_variables),
    detection_config: normalizeObject(next.detection_config, base.detection_config),
    detection_sources: normalizeNullableArray(next.detection_sources, base.detection_sources),
    eligibility_config: normalizeNullableObject(next.eligibility_config, base.eligibility_config),
    impact_formula: normalizeNullableObject(next.impact_formula, base.impact_formula),
    pipeline_steps: normalizePipelineSteps(next.pipeline_steps, meta.default_pipeline_steps),
    llm_config: normalizeNullableObject(next.llm_config, base.llm_config),
    column_definitions: normalizeArray(next.column_definitions, base.column_definitions),
    hidden_dimensions: normalizeArray(next.hidden_dimensions, base.hidden_dimensions),
    tab_definitions: normalizeNullableArray(next.tab_definitions, base.tab_definitions),
    visualization_config: normalizeNullableObject(
      next.visualization_config,
      base.visualization_config,
    ),
  }
}

export function extractEditablePayload(
  config: Partial<InsightConfig> = {},
): EditableInsightConfigPayload {
  const payload: Partial<EditableInsightConfigPayload> = {}
  for (const field of EDITABLE_INSIGHT_CONFIG_FIELDS) {
    Object.assign(payload, { [field]: cloneInsightValue(config[field]) })
  }
  return payload as EditableInsightConfigPayload
}

export function buildJsonEditors(
  config: Partial<InsightConfig> = {},
): Record<InsightConfigJsonField, string> {
  const entries = INSIGHT_CONFIG_JSON_FIELDS.map((field) => {
    const value =
      config[field] === undefined
        ? INSIGHT_CONFIG_JSON_FIELD_SPECS[field].defaultValue
        : config[field]
    return [field, JSON.stringify(value, null, 2)] as const
  })
  return Object.fromEntries(entries) as Record<InsightConfigJsonField, string>
}

export function parseJsonField(field: InsightConfigJsonField, value: unknown): unknown {
  const spec = INSIGHT_CONFIG_JSON_FIELD_SPECS[field]
  if (!spec) {
    throw new InsightConfigServiceError('insights.config.json.unknownField', field)
  }
  const trimmed = typeof value === 'string' ? value.trim() : ''
  if (!trimmed) return cloneInsightValue(spec.defaultValue)

  let parsed: unknown
  try {
    parsed = JSON.parse(trimmed)
  } catch {
    throw new InsightConfigServiceError('insights.config.json.invalid', field)
  }
  if (parsed === null) {
    if (spec.nullable) return null
    throw new InsightConfigServiceError('insights.config.json.nullForbidden', field)
  }
  if (spec.kind === 'array' && !Array.isArray(parsed)) {
    throw new InsightConfigServiceError('insights.config.json.arrayRequired', field)
  }
  if (spec.kind === 'object' && !isInsightRecord(parsed)) {
    throw new InsightConfigServiceError('insights.config.json.objectRequired', field)
  }
  return parsed
}

export function formatJsonText(field: InsightConfigJsonField, value: unknown): string {
  return JSON.stringify(parseJsonField(field, value), null, 2)
}

export function buildPatchPayload(
  currentPayload: Partial<EditableInsightConfigPayload> = {},
  originalPayload: Partial<EditableInsightConfigPayload> = {},
  meta: Partial<InsightConfigMeta> = {},
): Partial<EditableInsightConfigPayload> {
  const payload: Partial<EditableInsightConfigPayload> = {}
  for (const field of EDITABLE_INSIGHT_CONFIG_FIELDS) {
    const currentValue = currentPayload[field]
    const originalValue = originalPayload[field]
    if (
      field === 'pipeline_steps' &&
      originalValue == null &&
      insightValuesEqual(
        currentValue,
        normalizePipelineSteps(meta.default_pipeline_steps, meta.default_pipeline_steps),
      )
    ) {
      continue
    }
    if (!insightValuesEqual(currentValue, originalValue)) {
      Object.assign(payload, { [field]: cloneInsightValue(currentValue) })
    }
  }
  return payload
}

export function normalizePlatformValue(
  value: unknown,
  options: { fallback?: string } = {},
): string {
  const normalized = typeof value === 'string' ? value.trim() : ''
  if (!normalized) return options.fallback ?? 'all'
  return normalized.toLowerCase() === 'all' ? 'all' : normalized
}

export function normalizePlatformFilterValue(value: unknown): string {
  const normalized = normalizePlatformValue(value, { fallback: '' })
  return normalized === 'all' ? '' : normalized
}

/** Returns an i18n key for the sentinel, or the backend-authored platform label. */
export function formatPlatformLabel(
  value: unknown,
  options: { fallbackKey?: string } = {},
): string {
  const normalized = normalizePlatformValue(value, { fallback: '' })
  if (!normalized || normalized === 'all') return options.fallbackKey ?? 'insights.platform.all'
  return normalized
}

export function mapServerValidationErrors(
  error: ApiErrorFieldsLike | null | undefined,
): ConfigValidationErrors {
  const fields: Record<string, string[]> = {}
  const nonField: string[] = []
  const addMessages = (target: string[], value: unknown): void => {
    const messages = Array.isArray(value) ? value : [value]
    for (const message of messages) {
      if (typeof message === 'string' && message.trim()) target.push(message.trim())
    }
  }
  const visit = (value: unknown, allowTopLevelFields: boolean): void => {
    if (!isInsightRecord(value)) return
    const nestedError = value['error']
    if (isInsightRecord(nestedError)) visit(nestedError, false)
    else addMessages(nonField, nestedError)
    addMessages(nonField, value['detail'])
    addMessages(nonField, value['message'])
    addMessages(nonField, value['non_field_errors'])

    const nestedFields = isInsightRecord(value['fields'])
      ? value['fields']
      : isInsightRecord(value['errors'])
        ? value['errors']
        : null
    if (nestedFields) visit(nestedFields, true)
    if (!allowTopLevelFields) return
    for (const [field, messages] of Object.entries(value)) {
      if (['error', 'detail', 'message', 'non_field_errors', 'fields', 'errors'].includes(field)) {
        continue
      }
      const target = (fields[field] ??= [])
      addMessages(target, messages)
      if (target.length === 0) delete fields[field]
    }
  }

  visit(error?.payload, true)
  for (const [field, messages] of Object.entries(error?.fields ?? {})) {
    if (field === 'non_field_errors') addMessages(nonField, messages)
    else addMessages((fields[field] ??= []), messages)
  }
  addMessages(nonField, error?.detail)
  addMessages(nonField, error?.message)
  return {
    fields: Object.fromEntries(
      Object.entries(fields).map(([field, messages]) => [field, [...new Set(messages)]]),
    ),
    nonField: [...new Set(nonField)],
  }
}
